import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../../stores/builderStore';
import { fieldRegistry } from '../../../lib/fieldRegistry';
import type { FormField } from '../../../types/form';

interface MobileFormCanvasProps {
  onFieldSelect: (fieldId: string) => void;
  onAddField: () => void;
}

interface MobileFieldCardProps {
  field: FormField;
  index: number;
  totalFields: number;
  isSelected: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

function MobileFieldCard({
  field,
  index,
  totalFields,
  isSelected,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDelete,
  onEdit,
}: MobileFieldCardProps) {
  const { t } = useTranslation('mobile');
  const fieldDef = fieldRegistry[field.type];
  const isFirst = index === 0;
  const isLast = index === totalFields - 1;
  const [showActions, setShowActions] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchActive = useRef(false);

  // Cleanup timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  const handleTouchStart = useCallback(() => {
    isTouchActive.current = true;
    longPressTimer.current = setTimeout(() => {
      if (isTouchActive.current) {
        setShowActions(true);
      }
    }, 500);
  }, []);

  const handleTouchEnd = useCallback(() => {
    isTouchActive.current = false;
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTap = () => {
    if (!showActions) {
      onSelect();
      onEdit();
    }
  };

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 rounded-xl border-2 transition-all touch-manipulation ${
        isSelected
          ? 'border-primary-500 shadow-lg shadow-primary-500/20'
          : 'border-gray-200 dark:border-gray-700'
      }`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onClick={handleTap}
    >
      {/* Delete Action Overlay - Long Press */}
      {showActions && (
        <div
          className="absolute inset-0 z-20 bg-black/60 rounded-xl flex flex-col items-center justify-center gap-3 animate-fadeIn"
          onClick={(e) => {
            e.stopPropagation();
            setShowActions(false);
          }}
        >
          <p className="text-white text-sm font-medium px-4 text-center">
            {t('fieldConfigurator.deleteConfirm')}
          </p>
          <div className="flex gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(false);
              }}
              className="px-6 py-2 rounded-full bg-white text-gray-700 font-medium shadow-lg touch-manipulation"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              aria-label={t('fieldConfigurator.deleteField')}
              className="px-6 py-2 rounded-full bg-red-500 text-white font-medium shadow-lg touch-manipulation"
            >
              {t('common.delete')}
            </button>
          </div>
        </div>
      )}

      {/* Field Content */}
      <div className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg text-lg">
            {fieldDef.icon}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white truncate">
                {field.label}
              </span>
              {field.required && (
                <span className="text-red-500 text-sm flex-shrink-0">*</span>
              )}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {fieldDef.label}
            </span>
          </div>

          {/* Reorder Buttons */}
          <div className="flex flex-col gap-1 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
              disabled={isFirst}
              aria-label={t('formCanvas.moveUp')}
              className={`p-1 rounded touch-manipulation ${
                isFirst
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-500 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
              disabled={isLast}
              aria-label={t('formCanvas.moveDown')}
              className={`p-1 rounded touch-manipulation ${
                isLast
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-500 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Field Preview - Compact Version */}
        {field.type === 'text' && (
          <div className="h-10 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-3 flex items-center text-gray-400 text-sm">
            {field.placeholder || 'Text input'}
          </div>
        )}
        {field.type === 'email' && (
          <div className="h-10 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-3 flex items-center text-gray-400 text-sm">
            {field.placeholder || 'email@example.com'}
          </div>
        )}
        {field.type === 'textarea' && (
          <div className="h-16 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-gray-400 text-sm">
            {field.placeholder || 'Text area...'}
          </div>
        )}
        {field.type === 'select' && (
          <div className="h-10 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-3 flex items-center justify-between text-gray-400 text-sm">
            <span>Select option...</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
        {(field.type === 'checkbox' || field.type === 'radio') && (
          <div className="flex flex-wrap gap-2">
            {field.options?.slice(0, 3).map((opt, idx) => (
              <div
                key={`${idx}-${opt.value}`}
                className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300"
              >
                {opt.label}
              </div>
            ))}
            {field.options && field.options.length > 3 && (
              <div className="px-3 py-1.5 text-sm text-gray-400">
                +{field.options.length - 3} more
              </div>
            )}
          </div>
        )}
        {field.type === 'date' && (
          <div className="h-10 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-3 flex items-center justify-between text-gray-400 text-sm">
            <span>Select date</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {field.type === 'file' && (
          <div className="h-16 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400 text-sm">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload file
          </div>
        )}
        {field.type === 'section' && (
          <div className="border-l-4 border-primary-500 pl-3 py-2 bg-primary-50 dark:bg-primary-900/30 rounded-r-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300">Section divider</p>
          </div>
        )}
        {field.type === 'signature' && (
          <div className="h-20 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400 text-sm">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Signature
          </div>
        )}
        {field.type === 'rating' && (
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <svg key={i} className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            ))}
          </div>
        )}
      </div>

      {/* Position Indicator */}
      <div className="absolute top-2 right-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-500 dark:text-gray-400">
        {index + 1}/{totalFields}
      </div>
    </div>
  );
}

export default function MobileFormCanvas({ onFieldSelect, onAddField }: MobileFormCanvasProps) {
  const { t } = useTranslation('mobile');
  const {
    formSchema,
    selectedFieldId,
    selectField,
    removeField,
    moveFieldUp,
    moveFieldDown,
  } = useBuilderStore();

  const fields = formSchema.fields;

  const handleFieldSelect = useCallback((fieldId: string) => {
    selectField(fieldId);
    onFieldSelect(fieldId);
  }, [selectField, onFieldSelect]);

  return (
    <div className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-950 pb-24">
      {fields.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('formCanvas.empty')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs">
            {t('formCanvas.emptyDescription')}
          </p>
          <button
            onClick={onAddField}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-600/30 active:scale-95 transition-transform touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('formCanvas.addFirstField')}
          </button>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {/* Form Summary */}
          <div className="flex items-center justify-between px-2 mb-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('builder.fieldCount', { count: fields.length })}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12M8 12h12M8 17h12M3 7h.01M3 12h.01M3 17h.01" />
              </svg>
              {t('formCanvas.tapArrowsToReorder')}
            </span>
          </div>

          {/* Fields List */}
          {fields.map((field, index) => (
            <MobileFieldCard
              key={field.id}
              field={field}
              index={index}
              totalFields={fields.length}
              isSelected={selectedFieldId === field.id}
              onSelect={() => handleFieldSelect(field.id)}
              onMoveUp={() => moveFieldUp(field.id)}
              onMoveDown={() => moveFieldDown(field.id)}
              onDelete={() => removeField(field.id)}
              onEdit={() => handleFieldSelect(field.id)}
            />
          ))}

          {/* Add More Button */}
          <button
            onClick={onAddField}
            className="w-full py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2 active:bg-gray-50 dark:active:bg-gray-800 transition-colors touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('builder.addField')}
          </button>
        </div>
      )}
    </div>
  );
}
